import { useCallback, useMemo } from 'react';
import { useEntryState } from './history-memory';
import { type PageSlice, pageSlice } from './page-slice';

/**
 * The state of a table over the rows a screen HOLDS: which column orders it,
 * what the reader typed into the search box, and which page they are on.
 *
 * Why over the rows held, not the list: every list endpoint here is
 * cursor-paginated and carries no total (see page-slice.ts). A sort over the
 * 200 rows fetched is a sort over 200 rows, and the pager says so with the `+`
 * on the total. Nothing here fetches the whole list to order it.
 *
 * The rules, once, so three portals stop reading them three ways:
 *  - two directions, never back to unsorted. A new column starts in its FIRST
 *    direction (ascending for text, descending for figures - biggest first is
 *    what a reader wants of a count); the active column flips.
 *  - until the reader clicks, the rows keep the order the screen gave them.
 *  - blanks sink in both directions, as SQL's NULLS LAST would, and so does a
 *    value the column cannot order at all (see `blankFor`).
 *  - ties keep the server's order (Array.prototype.sort is stable).
 *  - text compares in the reader's locale, numerically aware, case-insensitive.
 *  - search folds accents and case, reads a comma as a period, and every
 *    whitespace-separated word must appear somewhere in the row.
 *  - any sort or search change returns the reader to page one; so does a
 *    change of `resetKey` (a server filter). A refetch that merely renews the
 *    rows does not, or a 15-second poll would throw them back to the start.
 */

export type SortDirection = 'asc' | 'desc';
export type SortValue = string | number | boolean | Date | null | undefined;
export type ColumnKind = 'text' | 'number' | 'date' | 'boolean';

interface TableColumnCommon<Row> {
  /** How to compare it, and which direction a first click takes. Default `text`. */
  readonly kind?: ColumnKind;
  /** Overrides the kind's first direction. */
  readonly first?: SortDirection;
  /**
   * What search reads for this column when it is not `String(value)`: a
   * formatted date, a money figure, a translated state.
   */
  readonly text?: (row: Row) => string;
}

/**
 * A column orders itself by a VALUE or by a COMPARATOR, and the two arms are
 * separate because a column that has only a comparator has nothing honest to
 * put in `value`.
 *
 * It used to have to invent one - `value: () => 0` was the idiom, and this
 * package's own shim wrote it - and that dummy did not stay in the sort: with
 * no `text`, search reads `String(value(row))` for every column, so each row's
 * searchable text gained a literal `0` and a client-side search for `0` matched
 * every row on the table, silently. A column with no `value` now contributes
 * nothing to that text unless it says what it contributes, with `text`.
 */
export type TableColumn<Row> = TableColumnCommon<Row> &
  (
    | {
        /** The value the column sorts on. */
        readonly value: (row: Row) => SortValue;
        /**
         * An ascending comparator when the value alone cannot say. The value is
         * still what decides BLANKNESS - it is read first, and a row whose value
         * is blank for the column's `kind` sinks before this ever runs.
         */
        readonly compare?: (a: Row, b: Row) => number;
      }
    | {
        readonly value?: undefined;
        /** A ranked order the row has no single value for. Ascending. */
        readonly compare: (a: Row, b: Row) => number;
      }
  );

export type TableColumns<Row, Key extends string> = Readonly<Record<Key, TableColumn<Row>>>;

export interface TableSort<Key extends string> {
  readonly key: Key;
  readonly direction: SortDirection;
}

export function isBlank(value: SortValue): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value))
  );
}

function toTime(value: SortValue): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return Date.parse(String(value));
}

/**
 * Blank AS THIS COLUMN READS IT: absent, or a value its own comparator cannot
 * turn into a number.
 *
 * `Number('n/a')` and `Date.parse('whenever')` are both `NaN`, and every
 * comparison with a `NaN` returns `NaN`, which `Array.prototype.sort` reads as
 * zero - so such a row does not sort at all, it simply stays where the stable
 * sort found it, scattered among values it is neither above nor below. A value
 * the column cannot order is as good as absent, and it sinks with the blanks.
 */
function blankFor(value: SortValue, kind: ColumnKind): boolean {
  if (isBlank(value)) return true;
  if (kind === 'number') return Number.isNaN(Number(value));
  if (kind === 'date') return Number.isNaN(toTime(value));
  return false;
}

/** Both values non-blank. Blanks are settled by the caller, before the direction sign. */
export function compareValues(
  a: SortValue,
  b: SortValue,
  kind: ColumnKind,
  collator: Intl.Collator,
): number {
  switch (kind) {
    case 'number':
      return Number(a) - Number(b);
    case 'date':
      return toTime(a) - toTime(b);
    case 'boolean':
      return Number(Boolean(a)) - Number(Boolean(b));
    default:
      return collator.compare(String(a), String(b));
  }
}

export function firstDirection<Row>(column: TableColumn<Row>): SortDirection {
  return column.first ?? (column.kind === 'number' ? 'desc' : 'asc');
}

export function nextSort<Key extends string>(
  current: TableSort<Key> | null,
  key: Key,
  first: SortDirection,
): TableSort<Key> {
  if (current !== null && current.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: first };
}

export function sortRows<Row, Key extends string>(
  rows: readonly Row[],
  columns: TableColumns<Row, Key>,
  sort: TableSort<Key> | null,
  locale?: string,
): readonly Row[] {
  if (sort === null) return rows;
  /*
   * Declared with `| undefined` because `Record<Key, ...>` is a mapped type and
   * `noUncheckedIndexedAccess` therefore says nothing about it - while a sort
   * restored from a URL or from storage can perfectly well name a column that
   * has since been renamed. Unsorted rows are the honest answer to a column
   * that is not there; throwing inside a render is not.
   */
  const column: TableColumn<Row> | undefined = columns[sort.key];
  if (column === undefined) return rows;
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
  const sign = sort.direction === 'asc' ? 1 : -1;
  const kind = column.kind ?? 'text';
  /*
   * A comparator-only column has no value to be blank, so it skips the blank
   * pass entirely: routing it through one would ask `blankFor(undefined)`, which
   * is true for every row, and every row would then tie with every other and
   * the comparator would never run.
   */
  const value = column.value;
  if (value === undefined) {
    return [...rows].sort((a, b) => sign * column.compare(a, b));
  }
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    const blankA = blankFor(va, kind);
    const blankB = blankFor(vb, kind);
    // Blanks last whichever way the column runs: decided BEFORE the sign.
    if (blankA || blankB) return blankA === blankB ? 0 : blankA ? 1 : -1;
    const base =
      column.compare === undefined ? compareValues(va, vb, kind, collator) : column.compare(a, b);
    return sign * base;
  });
}

const QUERY_CAP = 120;

export function foldText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .toLowerCase()
    .replace(/,/gu, '.');
}

export function searchTokens(query: string): readonly string[] {
  return foldText(query.trim().slice(0, QUERY_CAP))
    .split(/\s+/u)
    .filter((token) => token !== '');
}

/**
 * Both sides must already be FOLDED - `tokens` by `searchTokens`, `text` by
 * `foldText`, which is what `rowText` below returns. Handed raw row text this
 * compares an accented, cased string against folded tokens and simply misses,
 * with nothing to say it did: "Boulangerie Frédéric" never matches `frederic`.
 */
export function matchesQuery(text: string, tokens: readonly string[]): boolean {
  return tokens.every((token) => text.includes(token));
}

export function rowText<Row, Key extends string>(
  row: Row,
  columns: TableColumns<Row, Key>,
  extra?: (row: Row) => string,
): string {
  const parts: string[] = [];
  for (const column of Object.values(columns) as TableColumn<Row>[]) {
    if (column.text !== undefined) {
      parts.push(column.text(row));
    } else if (column.value !== undefined) {
      parts.push(String(column.value(row) ?? ''));
    }
    // The third case contributes nothing, deliberately: a comparator-only column
    // that has not said what it reads has no value to read, and inventing one is
    // what put a literal token in every row (see `TableColumn`).
  }
  if (extra !== undefined) parts.push(extra(row));
  return foldText(parts.join(' '));
}

export function searchRows<Row, Key extends string>(
  rows: readonly Row[],
  columns: TableColumns<Row, Key>,
  query: string,
  extra?: (row: Row) => string,
): readonly Row[] {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return rows;
  return rows.filter((row) => matchesQuery(rowText(row, columns, extra), tokens));
}

interface UseTableBaseOptions<Row, Key extends string> {
  /**
   * Module scope, or memoised. The sort memo and `sortBy` both key on this
   * object's IDENTITY, so a fresh literal on every render re-sorts every row on
   * every render.
   */
  readonly columns: TableColumns<Row, Key>;
  readonly initial?: TableSort<Key> | null;
  /**
   * `client`: the query narrows the rows held. `external`: the screen sends
   * the query to its endpoint itself and the rows arrive already narrowed; the
   * hook only carries the string so the toolbar can draw the box.
   */
  readonly search?: 'client' | 'external';
  /**
   * `client`: the hook orders the rows held. `external`: the screen sends the
   * sort to its endpoint itself and the rows arrive already ordered; the hook
   * only carries it so the headings can draw their arrows. Exactly the split
   * `search` above makes, and for a sharper reason.
   *
   * A sort over the rows HELD is honest while the rows held are the whole list,
   * and every list here is cursor-paged - so the moment one is longer than its
   * fetch, ordering it in the browser answers correctly about the wrong rows.
   * The operator's Orders list is the case that proved it: 817 orders, a first
   * page of 200 chosen by operational priority, and a reader clicking "newest
   * first" shown the newest of that page while the four newest days sat in
   * pages nobody had asked for (2026-09-07). A screen whose endpoint can order
   * the whole list should say so here and send `sort` with its query.
   */
  readonly sort?: 'client' | 'external';
  /** Module scope or memoised too, and for the same reason as `columns`. */
  readonly searchText?: (row: Row) => string;
  /**
   * The screen's own server filters, as one STABLE string per view: a change
   * returns the reader to page one. Stable is the load-bearing word - the page
   * is adjusted during the render that notices, so a key that differs on every
   * render (a timestamp, a freshly built object stringified) turns "always page
   * one" into `Too many re-renders`.
   */
  readonly resetKey?: string;
  readonly more?: boolean;
  /** How many rows exist, when the endpoint says - see `PageSliceOptions`. */
  readonly knownTotal?: number;
  readonly pageSize?: number;
  readonly locale?: string;
  /**
   * The name the table's sort, search and page are kept under for this history
   * entry, so a back step returns to them (`useEntryState`). Defaults to the
   * column keys, which is unique on every screen with one table; a screen that
   * draws two tables over the SAME columns names each.
   */
  readonly memoryKey?: string;
}

/**
 * The hook's options, plus the ordinary controlled/uncontrolled split over the
 * one piece of its state a screen can legitimately want to own: the query.
 *
 * Say nothing and the hook holds it, which is what every table here does. Pass
 * `query` and `onQueryChange` and the hook renders the value it is given and
 * calls back instead of writing to state - everything else is unchanged, the
 * page reset included: a controlled query returns the reader to page one on
 * exactly the same comparison an uncontrolled one does.
 *
 * BOTH OR NEITHER, which is why this is a union of two shapes rather than two
 * optional fields on one. A `query` with no `onQueryChange` is a search box
 * that cannot be typed in, and an `onQueryChange` with no `query` is a
 * callback fired at a value the caller never sees; both compile happily as
 * optional fields, and both are a screen that looks right until somebody uses
 * it. Here the half-controlled table is a typecheck failure.
 *
 * The one screen that needs it is the Activity feed, whose search box IS a deep
 * link. `?code=` is what an operator pastes into an incident thread to put
 * everybody on one order's events; it was an ordinary URL-backed filter until
 * the shared search replaced the box, and React state is the one place a URL
 * cannot restore a value from. So the STORAGE moves back out to the screen and
 * the behaviour stays here.
 */
export type UseTableOptions<Row, Key extends string> = UseTableBaseOptions<Row, Key> &
  (
    | {
        /** The search box's value, held by the screen. */
        readonly query: string;
        /** Where a keystroke goes. The hook writes no state of its own. */
        readonly onQueryChange: (next: string) => void;
      }
    | {
        readonly query?: undefined;
        readonly onQueryChange?: undefined;
      }
  );

export interface TableState<Row, Key extends string> {
  readonly rows: readonly Row[];
  readonly all: readonly Row[];
  readonly slice: PageSlice<Row>;
  readonly sort: TableSort<Key> | null;
  readonly sortBy: (key: Key) => void;
  readonly query: string;
  readonly setQuery: (next: string) => void;
  readonly page: number;
  readonly setPage: (next: number) => void;
  readonly columns: TableColumns<Row, Key>;
}

/** The page a reader chose, and the view they chose it for. */
interface RememberedPage<Key extends string> {
  readonly sort: TableSort<Key> | null;
  readonly query: string;
  readonly resetKey: string;
  readonly page: number;
}

function sameSort<Key extends string>(a: TableSort<Key> | null, b: TableSort<Key> | null): boolean {
  if (a === null || b === null) return a === b;
  return a.key === b.key && a.direction === b.direction;
}

export function useTable<Row, Key extends string>(
  rows: readonly Row[],
  options: UseTableOptions<Row, Key>,
): TableState<Row, Key> {
  const {
    columns,
    initial = null,
    search = 'client',
    sort: sortMode = 'client',
    searchText,
    resetKey = '',
    more,
    knownTotal,
    pageSize,
    locale,
    query: controlledQuery,
    onQueryChange,
    memoryKey,
  } = options;
  /*
   * Kept per history entry rather than in plain state: opening a row and
   * coming back re-mounts this table, and a reader who had sorted, searched and
   * paged is returned to exactly that (founder, 2026-09-13). Outside an
   * installed portal - a test, a static render - this is `useState`.
   */
  const memory = `table:${memoryKey ?? Object.keys(columns).join(',')}`;
  const [sort, setSort] = useEntryState<TableSort<Key> | null>(`${memory}:sort`, initial);
  /*
   * The query, held here unless the screen said it holds it (see
   * `UseTableOptions`). `ownQuery` is not a fallback COPY of a controlled
   * value: while a screen is controlling the box this stays at '' and is never
   * read, so there is one value and one writer, and no way for the two to sit a
   * keystroke apart.
   */
  const [ownQuery, setOwnQuery] = useEntryState(`${memory}:query`, '');
  const query = controlledQuery ?? ownQuery;
  const setQuery = useCallback(
    (next: string) => {
      if (onQueryChange === undefined) setOwnQuery(next);
      else onQueryChange(next);
    },
    [onQueryChange],
  );

  /*
   * The page is remembered TOGETHER with the view it was chosen for - the sort,
   * the query and the caller's reset key - and it is FORGOTTEN the moment that
   * view changes. Both halves matter. Keeping it is what stops a refetch from
   * throwing a reader back to page one every fifteen seconds; dropping it is
   * what stops a page coming back to life later: with the old page merely
   * ignored, clearing a search, flipping a heading twice or returning to a
   * filter put the reader back on a page they left two views ago.
   *
   * Compared field by field rather than through a joined string, because a
   * query or a reset key can contain the separator - the spec's own example is
   * `${scope}|${summary}` - and two different views would then stamp alike.
   *
   * Adjusted DURING the render that notices, which is React's own pattern for
   * state derived from previous renders: React re-runs this component with the
   * new value before it renders anything, so nothing paints. An effect would
   * paint one frame of page two of the new order first.
   */
  const [remembered, setRemembered] = useEntryState<RememberedPage<Key>>(`${memory}:page`, () => ({
    sort: initial,
    /*
     * The query AT MOUNT, not the empty string. An uncontrolled table cannot
     * tell the difference - its query starts empty - but a controlled one can
     * arrive with a value already in it, restored from the URL, and a `''`
     * here would make the first render disagree with itself about which view
     * the remembered page belongs to.
     */
    query,
    resetKey,
    page: 1,
  }));
  const sameView =
    remembered.query === query &&
    remembered.resetKey === resetKey &&
    sameSort(remembered.sort, sort);
  if (!sameView) setRemembered({ sort, query, resetKey, page: 1 });
  const page = sameView ? remembered.page : 1;
  const setPage = useCallback(
    (next: number) => setRemembered({ sort, query, resetKey, page: next }),
    [sort, query, resetKey],
  );

  const sortBy = useCallback(
    (key: Key) => {
      // Same reason as `sortRows`: a key that no longer names a column is a
      // dead heading, not a crash.
      const column: TableColumn<Row> | undefined = columns[key];
      if (column === undefined) return;
      setSort((current) => nextSort(current, key, firstDirection(column)));
    },
    [columns],
  );

  const all = useMemo(() => {
    const narrowed = search === 'client' ? searchRows(rows, columns, query, searchText) : rows;
    // `external` leaves the ORDER alone for the same reason it leaves the
    // narrowing alone: the endpoint has already applied it, to the whole list
    // rather than to this page of it, and re-applying it here could only
    // re-order the page against the list it was cut from.
    return sortMode === 'client' ? sortRows(narrowed, columns, sort, locale) : narrowed;
  }, [rows, columns, query, search, searchText, sort, sortMode, locale]);

  /*
   * Memoised for its IDENTITY, not for the arithmetic - slicing an array is
   * nothing. `slice.rows` is what a screen maps to `<tr>`s and hands to
   * `SelectAll`, and `[table.rows]` is the natural dependency list for the
   * effect every list screen here writes: prune the selection to what is
   * currently listed. A fresh array every render makes that effect run every
   * render - waste on most screens, and on the one that releases money an
   * effect that re-reads the whole selection each time anything at all
   * re-renders. `all` above is memoised for the same reason and this was not.
   */
  const slice = useMemo(
    () =>
      pageSlice(all, page, {
        ...(more === undefined ? {} : { more }),
        ...(knownTotal === undefined ? {} : { knownTotal }),
        ...(pageSize === undefined ? {} : { pageSize }),
      }),
    [all, page, more, knownTotal, pageSize],
  );

  return {
    rows: slice.rows,
    all,
    slice,
    sort,
    sortBy,
    query,
    setQuery,
    page: slice.page,
    setPage,
    columns,
  };
}
